import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["messages", "input", "form", "box", "launcher"]
  static values = { token: String }

  connect() {
    this.pendingNonces = new Set()
    this.historyLoaded = false
    this.ensureSession()
    // Restore open state
    if (localStorage.getItem('chatOpen') === '1') {
      this.open()
    }
  }

  ensureSession() {
    const token = this.tokenValue
    if (!token) {
      // Create a session server-side to get a token and cookie
      fetch("/chat_session", { method: "POST", headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content } })
        .then(r => r.json())
        .then(({ token }) => {
          this.tokenValue = token
          this.loadHistory().finally(() => this.subscribe(token))
        })
      return
    }
    this.loadHistory().finally(() => this.subscribe(token))
  }

  subscribe(token) {
    if (!this.consumer) this.consumer = createConsumer()

    // Avoid duplicate subscriptions for the same token
    if (this.subscription && this.currentToken === token) return

    if (this.subscription) {
      this.consumer.subscriptions.remove(this.subscription)
    }

    this.currentToken = token
    this.subscription = this.consumer.subscriptions.create(
      { channel: "ChatChannel", token: token },
      { received: (data) => this.appendMessage(data) }
    )
  }

  appendMessage({ sender, body, at, nonce }) {
    if (nonce && this.pendingNonces.has(nonce)) {
      // This message was already optimistically rendered; consume nonce and skip
      this.pendingNonces.delete(nonce)
      return
    }
    const li = document.createElement('li')
    li.className = `chat-msg chat-msg--${sender}`
    li.innerText = body
    this.messagesTarget.appendChild(li)
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  send(event) {
    event.preventDefault()
    const body = this.inputTarget.value.trim()
    if (!body) return
    const nonce = (crypto?.randomUUID?.() || (Date.now().toString(36) + Math.random().toString(36).slice(2)))
    this.pendingNonces.add(nonce)
    fetch("/chat_messages", {
      method: "POST",
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content },
      body: JSON.stringify({ message: { body, nonce } })
    }).then(() => {
      // Optimistic UI update so the user sees their message immediately
      this.appendMessage({ sender: "customer", body, at: new Date().toISOString(), nonce })
      this.inputTarget.value = ""
    }).catch(() => {
      // If the request failed, do not append; optionally show an error state
      this.pendingNonces.delete(nonce)
    })
  }

  disconnect() {
    if (this.subscription) {
      this.consumer?.subscriptions?.remove(this.subscription)
      this.subscription = null
    }
  }

  // UI controls
  toggle() { this.isOpen ? this.close() : this.open() }
  open() {
    this.boxTarget.classList.remove('d-none')
    this.isOpen = true
    localStorage.setItem('chatOpen', '1')
  }
  close() {
    this.boxTarget.classList.add('d-none')
    this.isOpen = false
    localStorage.setItem('chatOpen', '0')
  }

  // Fetch and render message history once
  loadHistory() {
    if (this.historyLoaded) return Promise.resolve()
    return fetch('/chat_messages.json', { headers: { 'Accept': 'application/json' } })
      .then(r => r.ok ? r.json() : [])
      .then(messages => {
        if (!Array.isArray(messages)) return
        this.messagesTarget.innerHTML = ''
        messages.forEach(m => this.appendMessage(m))
        this.historyLoaded = true
      }).catch(() => {})
  }
}
